import { parsePhoneNumberFromString } from "libphonenumber-js";

export type PhoneOption = { id: string; name: string };

const US_DEFAULT = { defaultCountry: "US" as const };

export function isValidUsStoredNumber(value: string): boolean {
  const parsed = parsePhoneNumberFromString(value, US_DEFAULT);
  return !!(parsed?.isValid() && parsed.country === "US");
}

/** `id` is E.164 for the API; `name` is a human-readable format from libphonenumber-js. */
export function phoneOptionFromE164(e164: string): PhoneOption {
  const parsed = parsePhoneNumberFromString(e164, US_DEFAULT);
  if (!parsed?.isValid() || parsed.country !== "US") {
    return { id: e164, name: e164 };
  }
  return {
    id: parsed.number,
    name: parsed.formatInternational(),
  };
}

/** Parses flexible US input; returns canonical E.164 or null. */
export function parseUsPhoneToE164(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) {
    return null;
  }
  const parsed = parsePhoneNumberFromString(trimmed, US_DEFAULT);
  if (!parsed?.isValid() || parsed.country !== "US") {
    return null;
  }
  return parsed.number;
}

export function getSmsAutocompleteStorageKey(siteId: string): string {
  return `sms_autocomplete_options_${siteId}`;
}

export function persistPhonesToKey(storageKey: string, phones: PhoneOption[]) {
  try {
    localStorage.setItem(storageKey, JSON.stringify(phones));
  } catch (error) {
    console.error("Failed to persist phone numbers: ", error);
  }
}

export function persistPhones(siteId: string, phones: PhoneOption[]) {
  persistPhonesToKey(getSmsAutocompleteStorageKey(siteId), phones);
}

export function loadPersistedPhonesFromKey(storageKey: string): PhoneOption[] {
  try {
    const stored = localStorage.getItem(storageKey);
    if (!stored) {
      return [];
    }
    const parsed = JSON.parse(stored) as unknown;
    if (!Array.isArray(parsed)) {
      return [];
    }
    const valid = parsed
      .map((entry): PhoneOption | null => {
        if (!entry || typeof entry !== "object") {
          return null;
        }
        const rec = entry as Record<string, unknown>;
        const id = rec.id;
        if (typeof id === "string" && isValidUsStoredNumber(id)) {
          return phoneOptionFromE164(id);
        }
        return null;
      })
      .filter((v): v is PhoneOption => v !== null);
    if (valid.length !== parsed.length || JSON.stringify(valid) !== stored) {
      persistPhonesToKey(storageKey, valid);
    }
    return valid;
  } catch (error) {
    console.error("Failed to load phone numbers from localStorage: ", error);
    return [];
  }
}

export function loadPersistedPhones(siteId: string): PhoneOption[] {
  return loadPersistedPhonesFromKey(getSmsAutocompleteStorageKey(siteId));
}
